import type { Definition, Objective, SessionEvent, VoteView } from '../api/missions';
import { ca } from '../i18n/ca';
import { derivePhases, objectiveDepths } from './phases';
import { describeEvent } from './useSessionLive';

/**
 * What a live session page reads out of the definition and the runtime's
 * events (UI doc 5.1): the objective in hand, the phase, the supports left, the
 * in-game hour, the latest decision from command, the timeline. Pure functions,
 * so each card is tested on its own; nothing here asks the runtime for
 * anything it does not already send.
 */

/** Objective states that mean "resolved": no longer the one in hand. */
export const RESOLVED_STATES = new Set(['done', 'failed', 'skipped', 'cancelled']);

type States = Map<string, { state: string; title: string; type?: string; position?: number[] }>;

/**
 * Whether the players cannot see the objective yet: its `visibility` when it
 * says one, else a `priority` of hidden (the platform's `ObjectiveRoles.hidden`,
 * read the same way). A scripted night writes `visibility: 'hidden'` on an
 * objective whose priority stays main, so the priority alone is not the question.
 */
export function isHiddenObjective(o: Pick<Objective, 'priority' | 'visibility'>): boolean {
  return o.visibility ? o.visibility === 'hidden' : o.priority === 'hidden';
}

/**
 * The objective types the runtime clocks in minutes (`defend_position`,
 * `observe_position`, `secure_hlz`: the `minutes` parameter).
 */
const HOLD_TYPES = new Set(['defend_position', 'observe_position', 'secure_hlz']);

/**
 * How long a hold objective asks for, in whole minutes, from its own `minutes`
 * parameter: null for any other type, for a hidden one (a player's copy carries
 * neither its type nor its parameters) and for a value that is no number. This
 * is the definition's figure, not a countdown: the runtime starts its clock when
 * somebody reaches the zone and says so in a `hold` event (`holdClockOf`, which
 * `NowCard` counts down from).
 */
export function holdMinutesOf(
  o: Pick<Objective, 'type' | 'params' | 'priority' | 'visibility'>,
): number | null {
  if (isHiddenObjective(o) || !HOLD_TYPES.has(o.type)) return null;
  const m = Number(o.params?.minutes);
  return Number.isFinite(m) && m > 0 ? Math.round(m) : null;
}

/** The runtime's last reading of how far the nearest player is from an objective (`distance` event). */
export interface DistanceNow {
  metres: number;
  /** When the platform received the reading, in ms: its age is `now` minus this. */
  receivedAt: number;
}

/**
 * The latest `distance` reading that has a row for the objective, or null when
 * the runtime has sent none (nobody on the server, the objective not active or
 * not given to this reader). The reading is the server's own measure of the
 * nearest living player (rule 7); no position ever reaches the page.
 */
export function distanceTo(events: SessionEvent[], id: string): DistanceNow | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.kind !== 'distance') continue;
    const rows = serverOf(e).rows;
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (Array.isArray(row) && row[0] === id && Number.isFinite(Number(row[1]))) {
        const at = receivedMs(e);
        return at === null
          ? null
          : { metres: Math.max(0, Math.round(Number(row[1]))), receivedAt: at };
      }
    }
  }
  return null;
}

/** A hold objective's clock as the runtime last reported it (`hold` event). */
export interface HoldClock {
  state: 'running' | 'paused';
  /** Seconds left when the report was received. */
  left: number;
  total: number;
  receivedAt: number;
}

/** The latest `hold` report of the objective, or null when its clock has not started. */
export function holdClockOf(events: SessionEvent[], id: string): HoldClock | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.kind !== 'hold') continue;
    const s = serverOf(e);
    if (s.id !== id) continue;
    const left = Number(s.left);
    const total = Number(s.total);
    const at = receivedMs(e);
    if (at === null || !Number.isFinite(left) || !Number.isFinite(total)) return null;
    return {
      state: s.state === 'running' ? 'running' : 'paused',
      left: Math.max(0, left),
      total: Math.max(0, total),
      receivedAt: at,
    };
  }
  return null;
}

/**
 * Whole seconds left on a hold clock at `now` (ms): a running clock counts down
 * from its last report by the time since it arrived, never below zero; a paused
 * one stands where it was reported.
 */
export function holdSecondsLeft(clock: HoldClock, now: number): number {
  if (clock.state !== 'running') return Math.ceil(clock.left);
  return Math.max(0, Math.ceil(clock.left - (now - clock.receivedAt) / 1000));
}

/**
 * The objectives a player is shown: never the enemy's own moves (`silent`), and
 * a hidden one only once the runtime has revealed it. Nobody gets another
 * answer on the session page, the mission's author and an admin included: the
 * direction page is where a director reads the rest.
 */
export function playerObjectives(objectives: Objective[], states: States): Objective[] {
  return objectives
    .filter((o) => !o.silent && (!isHiddenObjective(o) || states.has(o.id)))
    .map((o) => {
      // A player's copy of a hidden objective carries neither its place nor its type
      // (the platform's maskHidden); the runtime says both when it gives the objective.
      const given = states.get(o.id);
      if (!isHiddenObjective(o) || !given) return o;
      return {
        ...o,
        position: o.position ?? given.position,
        type: o.type ?? given.type ?? '',
      };
    });
}

/**
 * The objective in hand: the first unresolved one the player is shown, in the
 * graph's own order (shallowest dependency first, then as written), so a
 * second-phase objective never reads as current while the first is open.
 */
export function currentObjectiveOf(objectives: Objective[], states: States): Objective | undefined {
  const shown = playerObjectives(objectives, states);
  const depths = objectiveDepths(objectives);
  return shown
    .map((o, i) => ({ o, i, d: depths.get(o.id) ?? 0 }))
    .sort((a, b) => a.d - b.d || a.i - b.i)
    .map((x) => x.o)
    .find((o) => !RESOLVED_STATES.has(states.get(o.id)?.state ?? ''));
}

export interface PhaseNow {
  /** 1-based, as the pill reads it. */
  number: number;
  total: number;
  kind: 'insertion' | 'objectives' | 'extraction';
}

/**
 * The phase the night is in, from the derived phases (UI doc, decisions: never
 * authored). The insertion lasts until the runtime reports on an objective the
 * players are shown; the extraction starts once every one of them is
 * resolved; in between it is the phase of the objective in hand. The runtime
 * also reports the enemy's own moves (a silent QRF), which the player's
 * definition does not even carry (`maskHidden`): those end nothing.
 */
export function currentPhase(
  def: Pick<Definition, 'objectives' | 'points'>,
  states: States,
): PhaseNow {
  const phases = derivePhases(def);
  const total = phases.length;
  const reported = def.objectives.some((o) => !o.silent && states.has(o.id));
  if (!reported) return { number: 1, total, kind: 'insertion' };
  const current = currentObjectiveOf(def.objectives, states);
  if (!current) return { number: total, total, kind: 'extraction' };
  const at = phases.findIndex((p) => p.objectives.some((o) => o.id === current.id));
  return at < 0
    ? { number: 1, total, kind: 'insertion' }
    : { number: at + 1, total, kind: 'objectives' };
}

/** How many of the objectives a player is shown are resolved, for the NOW card's bar. */
export function progressOf(objectives: Objective[], states: States) {
  const shown = playerObjectives(objectives, states);
  return {
    resolved: shown.filter((o) => RESOLVED_STATES.has(states.get(o.id)?.state ?? '')).length,
    total: shown.length,
  };
}

/**
 * A six-figure grid reference, "045 112": hundreds of metres east and north of
 * the terrain's south-west corner, the way the platform's own map draws its
 * grid. A terrain whose config numbers its grid from another corner reads
 * differently in game (see the pull request that brought this in).
 */
export function gridRef(position: number[] | undefined): string | null {
  if (!position || position.length < 2) return null;
  const [x, y] = position;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const part = (m: number) => String(Math.max(0, Math.floor(m / 100))).padStart(3, '0');
  return `${part(x)} ${part(y)}`;
}

const serverOf = (e: SessionEvent) => (e.payload.server ?? {}) as Record<string, unknown>;
const lastOf = (events: SessionEvent[], kind: string) =>
  [...events].reverse().find((e) => e.kind === kind);
const receivedMs = (e: SessionEvent) => {
  const t = new Date(e.receivedAt).getTime();
  return Number.isFinite(t) ? t : null;
};

/** The kinds the platform writes itself, with no time of the runtime's. */
const PLATFORM_KINDS = new Set(['serverDown', 'restartLoop', 'preflight']);

/**
 * How much later than its own time an event must arrive to have waited for
 * the platform: the extension posts every two seconds, the log poll every ten
 * and takes over a minute after the extension goes quiet.
 */
export const AWAY_AFTER_MS = 90_000;

/** A stretch the platform heard nothing from a server that went on playing. */
export interface PlatformAway {
  /** When the first event that had to wait was said, ms. */
  from: number;
  /** When the platform had it, ms: it was back by then. */
  to: number;
  /** How many events waited. */
  events: number;
}

/**
 * The last time the platform was away while the night went on (outage test a,
 * staging session 9ktr5, 2026-10-18: three and a half minutes, caught up with
 * no hole, and the page said nothing). Read from the events themselves: each
 * says when it happened (`at`, seconds of the night) and the platform when it
 * arrived; the one that arrived soonest after its own time sets the night's
 * clock, and events that came `AWAY_AFTER_MS` or more after theirs waited in
 * the server's queue. Those said before the stretch's last one arrived are
 * one stretch. Null when nothing ever waited.
 */
export function lastPlatformAway(events: SessionEvent[]): PlatformAway | null {
  const timed = events.filter((e) => !PLATFORM_KINDS.has(e.kind) && receivedMs(e) !== null);
  if (timed.length === 0) return null;
  let zero = Infinity;
  for (const e of timed) zero = Math.min(zero, (receivedMs(e) ?? 0) - e.at * 1000);
  let away: PlatformAway | null = null;
  for (const e of timed) {
    const received = receivedMs(e) ?? 0;
    const said = zero + e.at * 1000;
    if (received - said < AWAY_AFTER_MS) continue;
    if (away && said < away.to) {
      away.to = Math.max(away.to, received);
      away.events += 1;
    } else {
      away = { from: said, to: received, events: 1 };
    }
  }
  return away;
}

/**
 * The runtime's periodic snapshot (`snapshot`, every five minutes by default):
 * the supports left, who is on the server and whether alive, and the in-game
 * hour. It is what the campaign persists, and the only count of what a support
 * has left that the runtime sends.
 */
/**
 * Whether a snapshot's player is a headless client, which is never a person on
 * a page nor counted as one. The same rule as the platform's `HeadlessClients`:
 * a Steam id is all digits and is nobody's headless client; the engine gives a
 * headless client an id of the form `HC12160`, or none, and the name
 * `headlessclient`. The platform's player copy has no id (nor the headless
 * client, which it already leaves out); a director's raw copy has both.
 */
export function isHeadlessClient(uid: unknown, name: unknown): boolean {
  const u = typeof uid === 'string' ? uid.trim() : '';
  if (/^[0-9]{6,32}$/.test(u)) return false;
  if (/^hc[0-9]*$/i.test(u)) return true;
  const n = typeof name === 'string' ? name.trim() : '';
  return /^(headless ?client|hc[0-9]{1,2})$/i.test(n);
}

export interface Snapshot {
  supports: Record<string, number>;
  players: { name: string; alive: boolean }[];
  daytime: number | null;
  receivedAt: number | null;
}

export function lastSnapshot(events: SessionEvent[]): Snapshot | null {
  const e = lastOf(events, 'snapshot');
  if (!e) return null;
  const s = serverOf(e);
  const supports: Record<string, number> = {};
  Object.entries((s.supports ?? {}) as Record<string, unknown>).forEach(([k, v]) => {
    if (typeof v === 'number') supports[k] = v;
  });
  // Only the name and whether alive: the snapshot carries more (a Steam id, the
  // whole loadout) that a page public by link has no business drawing.
  const players = (Array.isArray(s.players) ? (s.players as Record<string, unknown>[]) : [])
    .filter((p) => p && typeof p.name === 'string' && !isHeadlessClient(p.uid, p.name))
    .map((p) => ({ name: String(p.name), alive: p.alive !== false }));
  return {
    supports,
    players,
    daytime: typeof s.daytime === 'number' ? s.daytime : null,
    receivedAt: receivedMs(e),
  };
}

/**
 * The in-game hour, in minutes since midnight: the last snapshot's own
 * `daytime` carried forward by the wall clock; before the first snapshot, the
 * definition's starting hour carried forward from the runtime's start. The
 * runtime sets no time multiplier, so a minute here is a minute there. Null
 * while there is nothing to count from.
 */
export function inGameMinutes(
  def: Pick<Definition, 'time'>,
  events: SessionEvent[],
  now: number,
): number | null {
  const snap = lastSnapshot(events);
  let base: number | null = null;
  let since: number | null = null;
  if (snap && snap.daytime !== null && snap.receivedAt !== null) {
    base = snap.daytime * 60;
    since = snap.receivedAt;
  } else {
    const started = lastOf(events, 'runtimeStarted');
    const hour = def.time?.hour;
    if (started && typeof hour === 'number') {
      base = hour * 60;
      since = receivedMs(started);
    }
  }
  if (base === null || since === null) return null;
  const minutes = base + Math.max(0, now - since) / 60000;
  return ((Math.floor(minutes) % 1440) + 1440) % 1440;
}

/** "21:04", for the header's in-game hour. */
export function clockOfDay(minutes: number): string {
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(Math.floor(minutes / 60))}:${two(minutes % 60)}`;
}

/** Which fire-support system the runtime found on the server, from its start report. */
export function supportsSystemOf(events: SessionEvent[]): 'simplex' | 'vanilla' | null {
  const started = lastOf(events, 'runtimeStarted');
  const system = started ? serverOf(started).supports : undefined;
  return system === 'simplex' || system === 'vanilla' ? system : null;
}

export interface SupportState {
  key: string;
  /** Ready to call, cooling down, or a Simplex service whose clock the platform never reads. */
  state: 'ready' | 'cooling' | 'unlimited';
  /** When the cooldown ends, in ms since the epoch; null unless cooling. */
  readyAt: number | null;
}

/**
 * The supports card's pills. No support has a number of calls (the owner,
 * 2026-09-26): each is ready or in its cooldown, and the runtime's latest word
 * on that wins — a snapshot's seconds until ready per support, or a
 * supportUsed's own `readyIn` (a fire mission spent from the 0-8 menu, or
 * command clearing the clock), whichever came last. A Simplex service reads
 * "no limit" (`a-simplex-count-is-not-a-budget`), since its clock is its own.
 * Before the first word, a support of the definition is taken as ready.
 */
export function supportsState(
  supports: Record<string, number> | undefined,
  events: SessionEvent[],
  now: number,
): { rows: SupportState[]; fromSnapshot: boolean } {
  const there = Object.entries(supports ?? {})
    .filter(([, v]) => Number(v) > 0)
    .map(([k]) => k);
  const snap = lastSnapshot(events);
  const simplex = supportsSystemOf(events) === 'simplex';
  const keys = [...new Set([...there, ...Object.keys(snap?.supports ?? {})])];
  const latest = new Map<string, number>(); // seconds until ready, -1 for a Simplex service
  const saidAt = new Map<string, number>();
  events.forEach((e) => {
    const s = serverOf(e);
    const at = receivedMs(e) ?? now;
    if (e.kind === 'supportUsed' && typeof s.readyIn === 'number') {
      latest.set(String(s.support), s.readyIn);
      saidAt.set(String(s.support), at);
    } else if (e.kind === 'snapshot' && s.supports && typeof s.supports === 'object') {
      Object.entries(s.supports as Record<string, number>).forEach(([k, v]) => {
        latest.set(k, Number(v));
        saidAt.set(k, at);
      });
    }
  });
  const rows = keys.map((key) => {
    const known = latest.get(key);
    if (known === undefined) {
      return { key, state: simplex ? 'unlimited' : 'ready', readyAt: null } as SupportState;
    }
    if (known < 0) return { key, state: 'unlimited', readyAt: null } as SupportState;
    if (known === 0) return { key, state: 'ready', readyAt: null } as SupportState;
    const readyAt = (saidAt.get(key) ?? now) + known * 1000;
    return readyAt > now
      ? ({ key, state: 'cooling', readyAt } as SupportState)
      : ({ key, state: 'ready', readyAt: null } as SupportState);
  });
  return { rows, fromSnapshot: !!snap && Object.keys(snap.supports).length > 0 };
}

export interface CommandDecision {
  text: string;
  receivedAt: number | null;
}

/**
 * The latest decision command sent that the whole side wants to hear of
 * (UI doc 5.1, SUPPORTS card): a support granted, reinforcements on their way,
 * an objective added. Only what the runtime confirmed: a command it refused is
 * no news.
 */
export function latestCommandDecision(events: SessionEvent[]): CommandDecision | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    const s = serverOf(e);
    let text: string | null = null;
    if (e.kind === 'supportUsed' && s.granted === true) {
      const key = String(s.support);
      text = ca.sessionNow.supportReady(ca.supportNames[key] ?? key);
    } else if (e.kind === 'supportUsed' && Number(s.delta ?? 0) > 0) {
      const key = String(s.support);
      text = ca.sessionNow.supportGranted(Number(s.delta), ca.supportNames[key] ?? key);
    } else if (e.kind === 'objectiveAdded') {
      text = ca.sessionNow.objectiveAdded(String(s.title ?? s.id ?? ''));
    } else if (e.kind === 'commandAck' && s.ok && s.kind === 'sendReinforcements') {
      text = ca.sessionNow.reinforcements;
    }
    if (text) return { text, receivedAt: receivedMs(e) };
  }
  return null;
}

/** The latest line heard on the radio, "FROM: text", for the NOW card. */
export function latestRadio(events: SessionEvent[]): string | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const s = serverOf(events[i]);
    if (events[i].kind === 'log' && typeof s.radio === 'string' && s.radio.trim())
      return `${String(s.from ?? '')}: ${s.radio}`.replace(/^: /, '');
  }
  return null;
}

/**
 * Players on the server by the runtime's last health report, when it has sent
 * one. The engine counts a headless client among `players` once it has taken
 * its slot, and the report says how many beside it: people only, never the
 * headless client (the platform already leaves it out of a player's copy, and
 * says no more about it, so this takes off nothing there).
 */
export function playersOnServer(events: SessionEvent[]): number | null {
  const e = lastOf(events, 'health');
  if (!e) return null;
  const s = serverOf(e);
  const n = Number(s.players);
  if (!Number.isFinite(n)) return null;
  const headless = Number(s.headless);
  return Math.max(0, n - (Number.isFinite(headless) && headless > 0 ? headless : 0));
}

export interface TimelineRow {
  key: string;
  /** Mission minute, or null for a row the runtime did not time (a closed vote). */
  minute: number | null;
  /** The last minute of a row that stands for several (casualties in a row); absent on a single one. */
  minuteTo?: number;
  icon: string;
  text: string;
  /** Colour is the threshold (UI doc 3): ok and bad only for succeeded and failed. */
  tone?: 'ok' | 'bad';
  /**
   * The game's own class of whoever the row is about, for a tester's or an
   * admin's tooltip and nobody else's: the row itself never prints a class.
   */
  hint?: string;
  /** The rows this one stands for, oldest first, when it folds several. */
  items?: TimelineRow[];
}

/** Whose casualty it was, as the players' side reads it. */
export type CasualtySide = 'own' | 'enemy' | 'civilian';

/** The engine's own word for a side (`WEST`, `GUER`) and a definition's (`west`, `independent`), as one. */
function sideKey(side: unknown): string {
  const s = String(side ?? '').toLowerCase();
  if (s === 'guer' || s === 'resistance' || s === 'independent' || s === 'indep') return 'guer';
  if (s === 'civ' || s === 'civilian') return 'civ';
  return s;
}

/**
 * Whose a casualty was: a player is always ours, and so is anybody on the
 * players' own side; a civilian is a civilian; anybody else is the enemy's. An
 * event that names no side (a night played before the runtime sent it) reads
 * as the enemy's unless it was a player.
 */
export function casualtySide(e: SessionEvent, ownSide: string | undefined): CasualtySide {
  const s = serverOf(e);
  if (s.player) return 'own';
  const side = sideKey(s.side);
  if (side === 'civ') return 'civilian';
  return side !== '' && side === sideKey(ownSide) ? 'own' : 'enemy';
}

/** How many casualties each side took, for the figures of a night that is over. */
export function casualtyCounts(events: SessionEvent[], ownSide: string | undefined) {
  const counts = { own: 0, enemy: 0, civilian: 0 };
  events.filter((e) => e.kind === 'casualty').forEach((e) => counts[casualtySide(e, ownSide)]++);
  return counts;
}

/** The kinds a player's timeline shows; the rest are the director's and the rig's. */
const TIMELINE_ICONS: Record<string, string> = {
  objectiveState: 'flag',
  objectiveAdded: 'plus-circle',
  log: 'broadcast',
  supportUsed: 'box-seam',
  casualty: 'x-circle',
  twist: 'lightning',
  missionEnded: 'flag-fill',
  markerRevealed: 'geo-alt',
  intelRevealed: 'file-earmark-text',
  clock: 'hourglass-split',
  ordersTaken: 'folder2-open',
  vipLost: 'person-x',
  serverDown: 'exclamation-triangle',
  restartLoop: 'exclamation-triangle',
};

const textOf = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** A radio sender the runtime writes in capitals ("COMANDAMENT") as a word in a sentence ("Comandament"). */
const senderOf = (from: string) =>
  from && from === from.toUpperCase() ? from.charAt(0) + from.slice(1).toLowerCase() : from;

/**
 * One event as a sentence a person reads (the owner's review of 2026-10-12:
 * the timeline printed `rhsusf_usmc_recon_marpat_d_autorifleman`, `success`
 * and `intelRevealed`). A class is never text: a casualty is named by the
 * catalogue's own name for the class, which the platform sends beside it
 * (`victimName`), and by "soldat" when the catalogue does not know it.
 */
function timelineText(e: SessionEvent, opts: { names: boolean; ownSide?: string }): string {
  const s = serverOf(e);
  const t = ca.sessionTimeline;
  switch (e.kind) {
    case 'objectiveState': {
      const state = String(s.state ?? '');
      const title = String(s.title ?? s.id);
      return (
        t.objective[state]?.(title) ??
        t.objectiveOther(title, ca.objectiveUi.states[state] ?? state)
      );
    }
    case 'objectiveAdded':
      return t.objectiveAdded(String(s.title ?? s.id ?? ''));
    case 'supportUsed': {
      const key = String(s.support);
      return s.granted === true
        ? t.supportReady(ca.supportNames[key] ?? key)
        : t.supportGranted(Number(s.delta), ca.supportNames[key] ?? key);
    }
    case 'casualty': {
      const side = casualtySide(e, opts.ownSide);
      const who = s.player
        ? opts.names
          ? textOf(s.victim)
          : ''
        : textOf(s.victimName) || (side === 'civilian' ? '' : t.soldier);
      return t.casualty[side](who, !!s.player);
    }
    case 'intelRevealed':
      return t.intel(textOf(s.title));
    case 'markerRevealed':
      return t.marker(textOf(s.text));
    case 'twist':
      return t.twist(textOf(s.title));
    case 'missionEnded':
      return t.ended[String(s.outcome ?? '')] ?? t.endedOther;
    case 'clock':
      return s.phase === 'expired'
        ? t.clockExpired
        : t.clockLeft(Math.max(0, Math.round(Number(s.remaining ?? 0) / 60)));
    case 'ordersTaken': {
      const by = textOf(s.by);
      // A visitor's copy carries no name, so it cannot tell a hand from the clock.
      return by && opts.names ? t.ordersBy(by) : opts.names ? t.ordersByRadio : t.ordersTaken;
    }
    case 'vipLost':
      return t.vipLost;
    case 'log':
      return t.radio(senderOf(textOf(s.from)), textOf(s.radio));
    default:
      return describeEvent(e) || (ca.eventKinds[e.kind] ?? t.other);
  }
}

/** Casualties in a row fold when there are at least this many of one side. */
const FOLD_FROM = 2;

/**
 * The compact timeline (UI doc 5.1): objective results, radio messages, twists,
 * supports, reveals, the clock, newest first, each with its minute and an
 * icon; the closed decisions fold into it by the hour they closed, since they
 * leave the DECIDE card once closed. A log line that is not a radio message is
 * the runtime talking to whoever debugs it and stays off a player's page, as do
 * the health reports, the acknowledgements and the placement tables.
 *
 * Three things stay off it by who is reading. An objective the player is not
 * shown (`shown`: the enemy's silent QRF, a hidden one not yet revealed) is not
 * news from the runtime either; a player's in-game name, in a casualty, is for
 * somebody signed in only (`names`), the same rule as the roster's; and what a
 * twist was or what a piece of intel said is a surprise while the night runs
 * (`ended`), which the platform keeps out of a player's copy as well
 * (`SessionEventMask`): the page says that information was found, not which.
 *
 * Casualties of one side that follow each other fold into one row ("7 baixes
 * enemigues", minutes 44 to 52) that opens on the men it stands for: a
 * firefight is one piece of news, not forty. `classes` lets the game's own
 * class of each ride along as a tooltip, for a tester or an admin.
 */
export function timelineOf(
  events: SessionEvent[],
  votes: VoteView[],
  {
    shown,
    names,
    ownSide,
    classes = false,
    ended = true,
  }: {
    shown: Set<string>;
    names: boolean;
    /** The players' own side, as the definition writes it: tells our casualties from theirs. */
    ownSide?: string;
    classes?: boolean;
    /** Whether the night is over; a row that would tell a surprise waits for it. */
    ended?: boolean;
  },
  limit = 40,
): TimelineRow[] {
  const OBJECTIVE_KINDS = ['objectiveState', 'objectiveAdded'];
  type Timed = TimelineRow & { when: number; fold?: CasualtySide };
  const rows: Timed[] = events
    .filter((e) => TIMELINE_ICONS[e.kind])
    .filter((e) => e.kind !== 'log' || typeof serverOf(e).radio === 'string')
    .filter(
      (e) =>
        e.kind !== 'supportUsed' ||
        serverOf(e).granted === true ||
        Number(serverOf(e).delta ?? 0) > 0,
    )
    .filter((e) => !OBJECTIVE_KINDS.includes(e.kind) || shown.has(String(serverOf(e).id)))
    .filter((e) => ended || e.kind !== 'twist')
    .map((e) => {
      const s = serverOf(e);
      const state = String(s.state ?? '');
      const outcome = String(s.outcome ?? '');
      const tone =
        e.kind === 'objectiveState'
          ? state === 'done'
            ? ('ok' as const)
            : state === 'failed'
              ? ('bad' as const)
              : undefined
          : e.kind === 'missionEnded'
            ? outcome === 'success'
              ? ('ok' as const)
              : outcome === 'failed' || outcome === 'failure'
                ? ('bad' as const)
                : undefined
            : undefined;
      const fold = e.kind === 'casualty' ? casualtySide(e, ownSide) : undefined;
      // While the night runs, what was found stays unsaid even in a copy that carries it.
      const quiet =
        !ended && (e.kind === 'intelRevealed' || e.kind === 'markerRevealed')
          ? { ...e, payload: { server: {} } }
          : e;
      return {
        key: `e${e.seq}`,
        minute: Math.floor(e.at / 60),
        icon:
          fold === 'own'
            ? 'person-x'
            : fold === 'civilian'
              ? 'person-dash'
              : TIMELINE_ICONS[e.kind],
        text: timelineText(quiet, { names, ownSide }),
        tone,
        hint: classes && fold && !s.player && textOf(s.victim) ? textOf(s.victim) : undefined,
        when: receivedMs(e) ?? 0,
        fold,
      };
    });
  votes
    .filter((v) => v.state !== 'OPEN')
    .forEach((v) => {
      const at = new Date(v.closesAt ?? v.opensAt).getTime();
      rows.push({
        key: `v${v.id}`,
        minute: null,
        icon: 'check2-square',
        text: ca.sessionTimeline.voteClosed(v.question, v.result),
        when: Number.isFinite(at) ? at : 0,
      });
    });
  // Newest first; events of one instant keep the runtime's own order, reversed.
  const sorted = rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => b.r.when - a.r.when || b.i - a.i)
    .map(({ r }) => r);
  const plain = ({ key, minute, icon, text, tone, hint }: Timed): TimelineRow => ({
    key,
    minute,
    icon,
    text,
    tone,
    ...(hint ? { hint } : {}),
  });
  const out: TimelineRow[] = [];
  for (let i = 0; i < sorted.length;) {
    const first = sorted[i];
    let j = i + 1;
    if (first.fold) while (j < sorted.length && sorted[j].fold === first.fold) j++;
    const run = sorted.slice(i, j);
    if (first.fold && run.length >= FOLD_FROM) {
      const minutes = run.map((r) => r.minute ?? 0);
      const from = Math.min(...minutes);
      const to = Math.max(...minutes);
      out.push({
        key: `g${first.key}`,
        minute: from,
        ...(to > from ? { minuteTo: to } : {}),
        icon: first.icon,
        text: ca.sessionTimeline.casualties[first.fold](run.length),
        // Inside the fold the men read in the order they fell.
        items: run.map(plain).reverse(),
      });
    } else {
      out.push(plain(first));
    }
    i = j;
  }
  return out.slice(0, limit);
}

export type Connection = 'loading' | 'live' | 'stale' | 'disconnected';

/** No news for this long while the night is live reads as stale: the page polls every ten seconds. */
export const STALE_AFTER_MS = 30000;

/**
 * The four states of a live card (UI doc 7): loading until the first answer,
 * disconnected while the browser is offline or the platform stopped
 * answering, stale when a live night has sent nothing for thirty seconds, live
 * otherwise. A page that is not live polls nothing, so it is never stale.
 */
export function connectionOf({
  loaded,
  online,
  failing,
  live,
  lastReceivedAt,
  now,
}: {
  loaded: boolean;
  online: boolean;
  failing: boolean;
  live: boolean;
  lastReceivedAt: number | null;
  now: number;
}): Connection {
  if (!online || failing) return 'disconnected';
  if (!loaded) return 'loading';
  if (live && lastReceivedAt !== null && now - lastReceivedAt > STALE_AFTER_MS) return 'stale';
  return 'live';
}

/**
 * The order's support paragraph without its list of supports («a. Suports disponibles» and the
 * lines under it), which the briefing draws as tiles with their pictures; empty when nothing else
 * is left. The list printed the support's key («- transport») where the tiles name it.
 */
export function briefingSupportText(text: string | undefined): string {
  return (text ?? '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p && !/^a\.\s*Suports disponibles/i.test(p))
    .join('\n\n');
}
